import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  Select,
  TextField,
  MenuItem,
  Stack,
  Box,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { QRCodeSVG } from 'qrcode.react';
import api from '@/lib/api';
import { toast } from '@/lib/toast';
import { useAuth } from '@/lib/auth';
import useIsMobile from '@/lib/useIsMobile';
import MemberHistory from '@/components/MemberHistory';
import PhotoUploadField from '@/components/PhotoUploadField';
import ChipInputField from '@/components/ChipInputField';
import { TAGS_EXPLAINER } from '@/components/TagPill';

const LEAD_STAGES = [
  { value: 'new_lead', label: 'New Lead' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'trial_booked', label: 'Trial Booked' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost', label: 'Lost' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'lead', label: 'Lead' },
  { value: 'paused', label: 'Paused' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Split a stored full name into first / last for prefill when the record
// predates the split fields.
const splitName = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
};

const buildEmpty = (variant) => ({
  name: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  status: variant === 'lead' ? 'lead' : 'active',
  lead_stage: variant === 'lead' ? 'new_lead' : '',
  tags: [],
  notes: '',
  offers: '',
  custom_fields: {},
  photo_data_url: null,
});

const toForm = (member, variant) => {
  const split = splitName(member?.name);
  return {
    name: member?.name ?? '',
    first_name: member?.first_name ?? split.first,
    last_name: member?.last_name ?? split.last,
    email: member?.email ?? '',
    phone: member?.phone ?? '',
    status: member?.status ?? (variant === 'lead' ? 'lead' : 'active'),
    lead_stage: member?.lead_stage ?? (variant === 'lead' ? 'new_lead' : ''),
    tags: Array.isArray(member?.tags) ? member.tags : [],
    notes: member?.notes ?? '',
    offers: member?.offers ?? '',
    custom_fields: member?.custom_fields && typeof member.custom_fields === 'object' ? member.custom_fields : {},
    photo_data_url: member?.photo_data_url ?? null,
  };
};

const MemberDialog = ({
  open,
  mode = 'create',
  variant = 'member',
  member = null,
  onClose,
  onSaved,
}) => {
  const { isAdmin } = useAuth();
  const [formData, setFormData] = useState(() => buildEmpty(variant));
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState(0);
  const [createTab, setCreateTab] = useState('manual');
  const [leadForms, setLeadForms] = useState(null); // null = not loaded yet
  const [qrFormId, setQrFormId] = useState('');
  const [leadFields, setLeadFields] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [memberOptions, setMemberOptions] = useState([]);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (open) {
      setTab(0);
      setCreateTab('manual');
      setFormData(mode === 'edit' && member ? toForm(member, variant) : buildEmpty(variant));
      api
        .get('/members/tags')
        .then((res) => setTagOptions(Array.isArray(res.data) ? res.data : []))
        .catch(() => setTagOptions([]));
      // Union of custom field defs across the tenant's active lead forms.
      api
        .get('/lead-fields')
        .then((res) => setLeadFields(Array.isArray(res.data?.lead_fields) ? res.data.lead_fields : []))
        .catch(() => setLeadFields([]));
    }
  }, [open, mode, member, variant]);

  // Lazily fetch the tenant's lead forms the first time the QR tab is opened.
  useEffect(() => {
    if (open && createTab === 'qr' && leadForms === null) {
      api
        .get('/lead-forms')
        .then((res) => {
          const active = (Array.isArray(res.data) ? res.data : []).filter((f) => f.is_active);
          setLeadForms(active);
          if (active.length > 0) setQrFormId(active[0].form_id);
        })
        .catch(() => {
          setLeadForms([]);
          toast.error('Failed to load the lead forms');
        });
    }
  }, [open, createTab, leadForms]);

  const isLead = variant === 'lead' || formData.status === 'lead';
  const canLink = mode === 'edit' && member && member.status === 'lead';

  const openLink = async () => {
    setLinkTarget(null);
    setLinkOpen(true);
    try {
      const { data } = await api.get('/members', { params: { status: 'active' } });
      setMemberOptions(data.filter((m) => m.member_id !== member?.member_id));
    } catch {
      setMemberOptions([]);
    }
  };

  const handleLink = async () => {
    if (!linkTarget) return;
    setLinking(true);
    try {
      await api.post(`/members/${member.member_id}/merge-into/${linkTarget.member_id}`);
      toast.success(`Lead linked into ${linkTarget.name}`);
      setLinkOpen(false);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link lead');
    } finally {
      setLinking(false);
    }
  };

  const titleText = () => {
    if (mode === 'edit') return variant === 'lead' ? 'Edit lead' : 'Edit member';
    return variant === 'lead' ? 'Add new lead' : 'Add new member';
  };

  const submitLabel = () => {
    if (mode === 'edit') return 'Save changes';
    return variant === 'lead' ? 'Add lead' : 'Add member';
  };

  const setCustomField = (key, value) =>
    setFormData((prev) => ({ ...prev, custom_fields: { ...prev.custom_fields, [key]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.phone && !matchIsValidTel(formData.phone)) {
      toast.error('Enter a valid phone number, including country code');
      return;
    }
    setSubmitting(true);
    try {
      const fullName =
        variant === 'lead'
          ? [formData.first_name, formData.last_name].map((s) => s.trim()).filter(Boolean).join(' ')
          : formData.name;

      // Store number-typed custom fields as numbers, not input strings.
      const customFields = { ...(formData.custom_fields || {}) };
      for (const field of leadFields) {
        if (field.type === 'number' && customFields[field.key] !== undefined && customFields[field.key] !== '') {
          const n = Number(customFields[field.key]);
          if (!Number.isNaN(n)) customFields[field.key] = n;
        }
      }

      const payload = {
        name: fullName,
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        email: formData.email,
        phone: formData.phone,
        status: formData.status,
        tags: formData.tags,
        notes: formData.notes,
        offers: formData.offers,
        custom_fields: customFields,
        photo_data_url: formData.photo_data_url,
      };
      if (isLead) {
        payload.lead_stage = formData.lead_stage || 'new_lead';
      }

      if (mode === 'edit' && member) {
        await api.put(`/members/${member.member_id}`, payload);
        toast.success(variant === 'lead' ? 'Lead updated' : 'Member updated');
      } else {
        await api.post('/members', payload);
        toast.success(variant === 'lead' ? 'Lead added' : 'Member added');
      }
      onSaved?.();
      onClose();
    } catch (error) {
      const action = mode === 'edit' ? 'update' : 'add';
      const label = variant === 'lead' ? 'lead' : 'member';
      toast.error(error.response?.data?.detail || `Failed to ${action} ${label}`);
    } finally {
      setSubmitting(false);
    }
  };

  const showTabs = mode === 'edit' && isAdmin && !!member;
  // QR needs the lead-forms endpoint (staff+), so trainers keep the plain form.
  const showCreateTabs = mode === 'create' && isAdmin;
  const isMobile = useIsMobile();

  const qrForm = (leadForms || []).find((f) => f.form_id === qrFormId) || null;

  const copyFormLink = async () => {
    if (!qrForm) return;
    try {
      await navigator.clipboard.writeText(qrForm.url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontWeight: 700, pb: showTabs || showCreateTabs ? 0 : undefined }}>
        {titleText()}
      </DialogTitle>
      {showTabs && (
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Details" data-testid="member-tab-details" />
          <Tab label="History" data-testid="member-tab-history" />
        </Tabs>
      )}
      {showCreateTabs && (
        <Tabs
          value={createTab}
          onChange={(e, v) => setCreateTab(v)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="manual" label="Manual" data-testid="member-tab-manual" />
          <Tab value="qr" label="QR code" data-testid="member-tab-qr" />
        </Tabs>
      )}
      {showCreateTabs && createTab === 'qr' ? (
        <>
          <DialogContent>
            <Stack spacing={2} alignItems="center" sx={{ py: 2, textAlign: 'center' }}>
              {leadForms === null ? (
                <Box sx={{ py: 6, color: 'text.secondary', fontSize: 13 }}>Loading QR code…</Box>
              ) : !qrForm ? (
                <Box sx={{ py: 6, color: 'text.secondary', fontSize: 13 }}>
                  No active lead forms — create one under Settings → Lead forms.
                </Box>
              ) : (
                <>
                  {leadForms.length > 1 && (
                    <TextField
                      select
                      size="small"
                      label="Lead form"
                      value={qrFormId}
                      onChange={(e) => setQrFormId(e.target.value)}
                      sx={{ minWidth: 220 }}
                      data-testid="qr-form-select"
                    >
                      {leadForms.map((f) => (
                        <MenuItem key={f.form_id} value={f.form_id}>
                          {f.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <QRCodeSVG value={qrForm.url} size={220} data-testid="lead-form-qr" />
                  </Box>
                  <Box sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 360 }}>
                    Have them scan this with their phone camera — it opens your public lead form and
                    the submission lands in the Leads pipeline automatically.
                  </Box>
                  <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyFormLink}>
                    Copy form link
                  </Button>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      ) : showTabs && tab === 1 ? (
        <>
          <DialogContent>
            <MemberHistory memberId={member.member_id} />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      ) : (
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <PhotoUploadField
              value={formData.photo_data_url}
              onChange={(val) => setFormData({ ...formData, photo_data_url: val })}
            />
            {variant === 'lead' ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="First name"
                  required
                  fullWidth
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  data-testid="member-first-name-input"
                />
                <TextField
                  label="Last name"
                  fullWidth
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  data-testid="member-last-name-input"
                />
              </Box>
            ) : (
              <TextField
                label="Name"
                required
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="member-name-input"
              />
            )}
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="member-email-input"
            />
            <MuiTelInput
              label="Phone"
              required
              fullWidth
              defaultCountry="US"
              forceCallingCode
              value={formData.phone}
              onChange={(value) => setFormData({ ...formData, phone: value })}
              data-testid="member-phone-input"
            />
            {variant === 'member' && (
              <TextField
                select
                label="Status"
                fullWidth
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {isLead && (
              <TextField
                select
                label="Lead stage"
                fullWidth
                value={formData.lead_stage || 'new_lead'}
                onChange={(e) => setFormData({ ...formData, lead_stage: e.target.value })}
                data-testid="lead-stage-input"
              >
                {LEAD_STAGES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <ChipInputField
              label="Tags"
              fullWidth
              value={formData.tags}
              onChange={(tags) => setFormData({ ...formData, tags })}
              options={tagOptions}
              infoTip={TAGS_EXPLAINER}
              placeholder="Type a tag, press Enter"
            />
            <TextField
              label="Comments"
              fullWidth
              multiline
              minRows={2}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
            {isLead && (
              <TextField
                label="Offers"
                fullWidth
                multiline
                minRows={1}
                value={formData.offers}
                onChange={(e) => setFormData({ ...formData, offers: e.target.value })}
                placeholder="Free trial, 10% off first month…"
                data-testid="member-offers-input"
              />
            )}
            {isLead &&
              leadFields.map((field) => {
                const val = formData.custom_fields?.[field.key] ?? '';
                if (field.type === 'heading') {
                  // Display-only section heading — no stored value.
                  return (
                    <Box key={field.key}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {field.label}
                      </Typography>
                      {field.help_text && (
                        <Typography variant="caption" color="text.secondary">
                          {field.help_text}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (field.type === 'checkbox') {
                  return (
                    <Box key={field.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={val === true}
                            onChange={(e) => setCustomField(field.key, e.target.checked)}
                          />
                        }
                        label={field.required ? `${field.label} *` : field.label}
                      />
                      {field.help_text && (
                        <FormHelperText sx={{ mt: -0.5, ml: 4 }}>{field.help_text}</FormHelperText>
                      )}
                    </Box>
                  );
                }
                if (field.type === 'multiselect') {
                  const selectedVals = Array.isArray(val) ? val : [];
                  const labelId = `member-field-${field.key}-label`;
                  return (
                    <FormControl key={field.key} fullWidth required={field.required}>
                      <InputLabel id={labelId}>{field.label}</InputLabel>
                      <Select
                        labelId={labelId}
                        label={field.label}
                        multiple
                        value={selectedVals}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCustomField(field.key, typeof v === 'string' ? v.split(',') : v);
                        }}
                        renderValue={(vals) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {vals.map((v) => (
                              <Chip key={v} label={v} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {(field.options || []).map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            <Checkbox size="small" checked={selectedVals.includes(opt)} />
                            <ListItemText primary={opt} />
                          </MenuItem>
                        ))}
                      </Select>
                      {field.help_text && <FormHelperText>{field.help_text}</FormHelperText>}
                    </FormControl>
                  );
                }
                if (field.type === 'select') {
                  return (
                    <TextField
                      key={field.key}
                      select
                      label={field.label}
                      required={field.required}
                      fullWidth
                      value={val}
                      onChange={(e) => setCustomField(field.key, e.target.value)}
                      helperText={field.help_text || undefined}
                    >
                      {!field.required && (
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                      )}
                      {(field.options || []).map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }
                const inputType =
                  field.type === 'number'
                    ? 'number'
                    : field.type === 'email'
                      ? 'email'
                      : field.type === 'phone'
                        ? 'tel'
                        : field.type === 'date'
                          ? 'date'
                          : 'text';
                return (
                  <TextField
                    key={field.key}
                    label={field.label}
                    required={field.required}
                    fullWidth
                    type={inputType}
                    multiline={field.type === 'textarea'}
                    minRows={field.type === 'textarea' ? 2 : undefined}
                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                    placeholder={field.placeholder || undefined}
                    helperText={field.help_text || undefined}
                    value={val}
                    onChange={(e) => setCustomField(field.key, e.target.value)}
                  />
                );
              })}
          </Stack>
        </DialogContent>
        <DialogActions>
          {canLink && (
            <Button
              startIcon={<LinkIcon />}
              onClick={openLink}
              disabled={submitting}
              sx={{ mr: 'auto' }}
              data-testid="link-to-member-button"
            >
              Link to existing member…
            </Button>
          )}
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting} data-testid="member-submit-button">
            {submitLabel()}
          </Button>
        </DialogActions>
      </form>
      )}

      <Dialog open={linkOpen} onClose={() => !linking && setLinkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Link lead to existing member</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Use this when this lead is a duplicate of someone who is already a member. The lead&apos;s
            appointments, invoices, messages, tags and notes move onto the member, and this lead
            record is removed. This can&apos;t be undone.
          </DialogContentText>
          <Autocomplete
            options={memberOptions}
            value={linkTarget}
            onChange={(e, v) => setLinkTarget(v)}
            getOptionLabel={(m) => (m ? `${m.name} — ${m.email}` : '')}
            isOptionEqualToValue={(a, b) => a.member_id === b.member_id}
            renderInput={(params) => (
              <TextField {...params} label="Member" placeholder="Search members…" autoFocus />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)} disabled={linking}>
            Cancel
          </Button>
          <Button
            onClick={handleLink}
            variant="contained"
            disabled={linking || !linkTarget}
            data-testid="confirm-link-button"
          >
            {linking ? 'Linking…' : 'Link & merge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default MemberDialog;
